import { Injectable } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import moment from 'moment';
import {
	BehaviorSubject, filter, firstValueFrom, Observable, pairwise,
	Subscription
} from 'rxjs';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

import { BocClient } from '../../shared/models/boc-client-transac';
import { LocationService } from '../../shared/services/location.service';
import { TranslationService } from '../../shared/services/translation.service';
import {
	ProductCharacteristicsTranslationService
} from '../../shared/services/product-characteristics-translation.services';
import { ErrorService } from '../../shared/services/error.service';
import { DATETIME_FORMAT_PATTERN, WEEKYEAR_FORMAT_PATTERN,
	DATE_FORMAT_PATTERN_DD_MM_YYYY } from '../../shared/models/filter-formats';
import {
	SnackbarSubmissionFeedbackComponent
} from '../../shared/components/snackbar-submission-feedback/snackbar-submission-feedback.component';
import { AddToCartResponse, TemplateVariableArray, PRODUCT_TYPE_NF, RequestProductData,
	VariableField, EorderProduct, Eorder, RequestOrder, FIELD_THICKNESS, FIELD_WIDTH, FORM_SHEET_CODE,
	FIELD_SHIPTO_PARTY, FIELD_STEEL_GRADE, FIELD_LENGTH, FIELD_FINISHING_LINE, RequestOrderItem,
	TemplateVariable, Partner, RequestServiceLevel, RDD_TYPE_WEEK_VAL, PRODUCT_TYPE_F,
	FIELD_USE_PART_REFERENCE } from '../models/eorder';
import { ProductConfiguration, CharacteristicValueSimpleObject,
	TemplateVariableValues } from '../../shared/models/product';
import { EorderWizardState, EorderWizardStep } from '../models/eorder-wizard-state';

import { EorderWizardDb } from './wizard.database';
import { EorderApiService } from './eorder.api.service';

export const PAGE_NAME_STEPS = ['orderInformation', 'productModification', 'summary'];

@Injectable({ providedIn: 'root' })
export class EorderWizardService {
	previousUrl: string | undefined;
	urlData: string[] = [];
	backViewSubscription?: Subscription;
	totalCartItems = 0;
	navigationCount = 0;
	requestOrderObj!: RequestOrder;
	orderId!: string;
	orderIdObs: Observable<string>;
	shipToList?: BocClient[];
	partnerInfo?: Map<string, Partner>;
	boc!: BocClient;
	readonly variableValuesIndexedByTemplateNumber = new Map<number, Map<string, TemplateVariableValues>>();
	valuesPossiblePerFieldByTemplateNumber = new Map<number, VariableField[]>();
	optionalToleranceTemplateGroup = new Map<number, Map<string, VariableField[]>>();
	thirdRowsVariables = new Map<number, VariableField[]>();
	private readonly orderIdSubject = new BehaviorSubject(<string>'');
	private state: EorderWizardState;

	constructor(
		private readonly router: Router,
		private readonly db: EorderWizardDb,
		private readonly translationService: TranslationService,
		private readonly eorderApiService: EorderApiService,
		private readonly locationService: LocationService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly errorService: ErrorService,
		private readonly snackBar: MatSnackBar,
	) {
		this.state = new EorderWizardState(this.createDefaultOrder());
		this.orderIdObs = this.orderIdSubject.asObservable();
	}

	getState() {
		return this.state;
	}

	getStateEorderForm() {
		return this.state.order.form;
	}

	getPartnerInfo(shipto: string) {
		return this.partnerInfo?.get(shipto);
	}

	getAllPartnerInfo() {
		return this.partnerInfo;
	}

	createDefaultOrder() {
		return {
			header: {}
		};
	}

	prepareVariableFormGroup(templateNumber: number, configData: ProductConfiguration, itemCount: number): FormGroup[] {
		const templateDetails = this.getState().ui.getTemplateNumberDetails(
			templateNumber
		);
		const variableFormGroupList: FormGroup[] = [];
		const variableFormGroup = new FormGroup({});
		if (templateDetails) {
			for (const variableName of Object.getOwnPropertyNames(templateDetails.properties)) {
				const variable = templateDetails.properties[variableName];
				this.prepareVariableFormGroupByType(
					variableFormGroup,
					variableName,
					configData,
					variable
				);
			}
		}
		variableFormGroupList.push(variableFormGroup);
		for (let i = 1; i < itemCount; i++) {
			const duplicateVariableFormGroup = new FormGroup({});
			for (const variableKey of Object.keys(variableFormGroup.controls)) {
				const variableControl = variableFormGroup.get(variableKey);
				if (variableControl instanceof FormControl) {
					if (variableControl.hasValidator(Validators.required)) {
						duplicateVariableFormGroup.addControl(
							variableKey,
							new FormControl(variableControl.value, Validators.required)
						);
					} else {
						duplicateVariableFormGroup.addControl(variableKey, new FormControl(variableControl.value));
					}
				} else if (variableControl instanceof FormArray) {
					const formArray = new FormArray<FormControl>([]);
					for (const control of variableControl.controls) {
						if (control.hasValidator(Validators.required)) {
							formArray.push(new FormControl(control.value, Validators.required));
						} else {
							formArray.push(new FormControl(control.value));
						}
					}
					duplicateVariableFormGroup.addControl(variableKey, formArray);
				}
			}
			variableFormGroupList.push(duplicateVariableFormGroup);
		}
		return variableFormGroupList;
	}

	addToCart(
		subCategory: string,
		form: string,
		configData: ProductConfiguration,
		itemCount: number,
		templateNumber: string
	): AddToCartResponse {
		const formProducts = this.state.order.form.getFormProducts();
		const productToAdd: EorderProduct = {
			productType: this.removeTranslationPrefix(configData.productType),
			templateNumber: Number(templateNumber),
			configurationNumber: configData.configurationNumber
		};
		if (productToAdd.productType !== PRODUCT_TYPE_NF) {
			productToAdd.a2citemNumber = configData.a2CItemNumber;
			productToAdd.inquiryNumber = configData.inquiryNumber;
			productToAdd.steelGrade = configData.steelGradeCode;
			productToAdd.thicknessValue = Number(configData.thicknessValue);
			productToAdd.widthValue = Number(configData.widthValue);
			productToAdd.lengthValue = Number(configData.lengthValue);
			productToAdd.shipToParty = this.getDefaultShipTo(configData.shipToParty);
		}
		if (configData.steelGrade) {
			productToAdd.steelGradeDescription = this.productCharacteristicsTranslationService
				.translate(configData.steelGrade);
		}
		const varaibleFormGroupArray = this.prepareVariableFormGroup(
			Number(templateNumber),
			configData,
			itemCount
		);
		const response: AddToCartResponse = { itemAdded: false, allowAfterConfirmation: false, messages: [] };
		if (itemCount > 0) {
			if (formProducts.length > 0) {
				if (this.state.order.form.getSubCategory() !== subCategory) {
					response.itemAdded = false;
					response.allowAfterConfirmation = true;
					response.messages.push(
						this.translationService.getTR('eorder.add.item.different.family.warning'),
						this.translationService.getTR('eorder.add.item.confirm.action')
					);
				} else if (this.state.order.form.getFormValue() !== form) {
					response.itemAdded = false;
					response.allowAfterConfirmation = true;
					response.messages.push(
						this.translationService.getTR('eorder.add.item.different.form.warning'),
						this.translationService.getTR('eorder.add.item.confirm.action')
					);
				} else if ((this.getProductCount() + itemCount) > 100) {
					response.itemAdded = false;
					response.messages.push(this.translationService.getTR('eorder.add.item.limit.exeeds.warning')
						.replace('[X]', String(itemCount)));
				} else {
					response.itemAdded = true;
					formProducts.addProduct(productToAdd, form, itemCount, varaibleFormGroupArray);
				}
			} else if (itemCount > 100) {
				response.itemAdded = false;
				response.messages.push(this.translationService.getTR('eorder.add.item.limit.exeeds.warning')
					.replace('[X]', String(itemCount)));
			} else {
				response.itemAdded = true;
				formProducts.addProduct(productToAdd, form, itemCount, varaibleFormGroupArray);
				this.state.order.form.setSubCategory(subCategory);
				this.state.order.form.setFormValue(form);
				this.state.order.form.setCategory(this.removeTranslationPrefix(configData.category));
			}
		}
		return response;
	}

	getDefaultShipTo(shipto?: string): string | undefined {
		if (this.shipToList && this.shipToList.length > 0 && shipto) {
			const shipToValueToCheck = shipto.replace(/^0+/, '');
			const defaultShipTo = this.shipToList.find(v => v.businessId === shipToValueToCheck);
			if (defaultShipTo) {
				return defaultShipTo.businessId;
			}
		}
		return undefined;
	}

	getProductCount() {
		return this.state.order.form.getFormProducts().getProductCount();
	}

	isCartNotEmpty() {
		return this.state.order.form.getFormProducts().length > 0;
	}

	async actionGoNextStep() {
		if (this.state.stepIndex >= EorderWizardStep.CONFIRMATION) {
			return;
		}
		// push navigation step
		this.state.stepIndex += 1;

		const url = `order/wizard/${ PAGE_NAME_STEPS[this.state.stepIndex] }`;
		if (this.state.stepIndex !== EorderWizardStep.CONFIRMATION) {
			this.navigationCount++;
			await this.router.navigateByUrl(url);
		} else {
			this.locationService.go(-this.state.stepIndex, url);
		}
	}

	setOrderId(orderId: string) {
		this.orderIdSubject.next(String(orderId));
	}

	actionToGoBack() {
		this.backViewSubscription = this.router.events
			.pipe(
				filter((ev): ev is RoutesRecognized => ev instanceof RoutesRecognized),
				pairwise()
			)
			.subscribe(ev => {
				for (const url of ev) {
					this.urlData.push(url.urlAfterRedirects);
				}
			});
		const disconIdx = this.urlData.indexOf('/order/wizard/orderInformation');
		this.navigationCount = 1;
		return this.urlData[disconIdx - 1];
	}

	actionRetrievePreviousPage() {
		return this.previousUrl;
	}

	getNavigationCount() {
		return this.navigationCount;
	}

	async actionMoveUsingSteps(step: EorderWizardStep) {
		if (this.state.stepIndex <= EorderWizardStep.ORDERINFORMATION) {
			return;
		}
		if (step < this.state.stepIndex) {
			this.state.stepIndex = 0;
			this.navigationCount++;
			const url = `order/wizard/${ PAGE_NAME_STEPS[step] }`;
			await this.router.navigateByUrl(url);
		}
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	cleanCart() {
		this.state.order.form.setCategory(undefined);
		this.state.order.form.setSubCategory(undefined);
		this.state.order.form.setFormValue(undefined);
		this.state.order.form.getFormProducts().removeAllProducts();
	}

	async actionExitWizard() {
		this.variableValuesIndexedByTemplateNumber.clear();
		this.valuesPossiblePerFieldByTemplateNumber.clear();
		this.thirdRowsVariables.clear();
		this.optionalToleranceTemplateGroup.clear();
		await this.db.removeOffer(); // not in use
		return true;
	}

	checkWizardStateValidity(stepIndex: EorderWizardStep) {
		this.state.stepIndex = stepIndex;
		if (this.state.isValid) {
			return true;
		}
		if (this.state.exiting) {
			this.state.exiting = false;
			return true;
		}
		const defaultOrder = this.createDefaultOrder();
		this.initOrder(defaultOrder);
		this.locationService.back('order/wizard');
		return false;
	}

	async actionEditEordrDraft(orderId: number) {
		try {
			this.requestOrderObj = await firstValueFrom(this.eorderApiService.getEordrDraft(orderId));
			this.state.isValid = true;
			const url = `order/wizard/${ PAGE_NAME_STEPS[0] }`;
			return await this.router.navigateByUrl(url);
		} catch (err) {
			console.error('unable to fetch Eorder Draft', err);
			return false;
		}
	}

	getValuesPossiblePerField(templateNumber: number): VariableField[] {
		return this.valuesPossiblePerFieldByTemplateNumber.get(templateNumber) || [];
	}

	populateValuesPossiblePerField(templateNumber: number): VariableField[] {
		let fieldKeyValues = this.valuesPossiblePerFieldByTemplateNumber.get(templateNumber);
		if (fieldKeyValues === undefined) {
			const templateAsMap = this.variableValuesIndexedByTemplateNumber.get(templateNumber);
			fieldKeyValues = [];
			if (templateAsMap) {
				for (const [key, value] of templateAsMap) {
					const fields = {
						keys: key,
						values: value.values,
						type: value.type,
						freeType: value.freeType,
						maxItems: value.maxItems,
						parentVariableName: value.parentVariableName,
						mandatory: value.mandatory,
						hidden: value.hidden,
						group: value.group,
						order: value.order
					};
					fieldKeyValues.push(fields);
				}
				this.valuesPossiblePerFieldByTemplateNumber.set(templateNumber, fieldKeyValues);
			}
		}
		return fieldKeyValues;
	}

	getVariablesForThirdRow(templateNumber: number) {
		return this.thirdRowsVariables.get(templateNumber) || [];
	}

	populateVariablesForThirdRow(templateNumber: number) {
		if (!this.thirdRowsVariables.has(templateNumber)) {
			const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
			this.thirdRowsVariables.set(templateNumber, this.filterVariablesForThirdRow(valuesPossiblePerField));
		}
	}

	filterVariablesForThirdRow(fieldKeyValues: VariableField[]) {
		return fieldKeyValues
			.filter(fieldKeyValue =>
				fieldKeyValue.keys !== FIELD_STEEL_GRADE
				&& fieldKeyValue.keys !== FIELD_SHIPTO_PARTY
				&& fieldKeyValue.keys !== FIELD_THICKNESS
				&& fieldKeyValue.keys !== FIELD_WIDTH
				&& fieldKeyValue.keys !== FIELD_LENGTH
				&& fieldKeyValue.keys !== FIELD_FINISHING_LINE
				&& fieldKeyValue.group === undefined
				&& fieldKeyValue.order === undefined)
			.sort((a, b) => a.keys.localeCompare(b.keys));
	}

	getOptionalToleranceFields(templateNumber: number) {
		return this.optionalToleranceTemplateGroup.get(templateNumber) || new Map<string, VariableField[]>();
	}

	populateVariablesForOptionalTolerance(templateNumber: number) {
		if (this.optionalToleranceTemplateGroup.has(templateNumber)) {
			return;
		}
		const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
		for (const fieldKeyValue of valuesPossiblePerField) {
			if (fieldKeyValue.group && fieldKeyValue.order) {
				let currentTemplateGroup = this.optionalToleranceTemplateGroup.get(templateNumber);
				if (currentTemplateGroup) {
					const optionalToleranceGroup = currentTemplateGroup.get(fieldKeyValue.group);
					if (optionalToleranceGroup) {
						optionalToleranceGroup.push(fieldKeyValue);
					} else {
						currentTemplateGroup.set(fieldKeyValue.group, [fieldKeyValue]);
					}
				} else {
					currentTemplateGroup = new Map<string, VariableField[]>();
					this.optionalToleranceTemplateGroup.set(templateNumber, currentTemplateGroup);
					currentTemplateGroup.set(fieldKeyValue.group, [fieldKeyValue]);
				}
			}
		}
		for (const [, value] of this.optionalToleranceTemplateGroup.entries()) {
			for (const [groupName, fields] of value.entries()) {
				const sortedFields = fields.toSorted((a, b) => a.order && b.order ? a.order - b.order : 0);
				value.set(groupName, sortedFields);
			}
		}
	}

	async viewEordrDraft(orderId: number) {
		try {
			const url = `order/orders/${ orderId }`;
			await this.router.navigateByUrl(url);
			return true;
		} catch (err) {
			console.error('unable to fetch Eorder Draft', err);
			return false;
		}
	}

	async actionNewOrder() {
		try {
			const defaultOrder = this.createDefaultOrder();
			this.initOrder(defaultOrder);
			await this.db.removeOffer();
			const currentUrl = this.router.url;
			await (currentUrl.includes('order/wizard')
				? this.router.navigateByUrl('/', { skipLocationChange: true }).then(async () => {
					await this.router.navigate([currentUrl]);
				}).catch((err: unknown) => {
					console.warn('unable to navigate to order wizard', err);
				})
				: await this.router.navigateByUrl('order/wizard'));
			return true;
		} catch (err) {
			console.error('unable to create new order', err);
			return false;
		}
	}

	checkVariableValuesExistByTemplateNumberAndVariableName(templateNumber: number, variableName: string): boolean {
		const templateAsMap = this.variableValuesIndexedByTemplateNumber.get(templateNumber);
		return templateAsMap?.has(variableName) || false;
	}

	showMessage(msg: string) {
		this.snackBar.open(
			msg,
			undefined,
			{ duration: 5000 }
		);
	}

	showError(msg?: string) {
		this.snackBar.open(
			msg === undefined
				? this.translationService.getTR('quapp.error')
				: `${ this.translationService.getTR('quapp.error') } - ${ msg }`,
			undefined,
			{ duration: 5000 }
		);
	}

	prepareVariableFormGroupArray(
		formArray: FormArray,
		arrayObject: CharacteristicValueSimpleObject[],
		variableArray: TemplateVariableArray,
		mandatory: boolean
	) {
		for (const entryVariableName of Object.getOwnPropertyNames(variableArray.properties)) {
			// contentChildFieldName from templateDetails
			for (const obj of arrayObject) {
				if (obj[entryVariableName]) { // contentChildFieldName value from configData
					let value = obj[entryVariableName];
					if (value && !variableArray.properties[entryVariableName].free
						&& typeof value === 'string') {
						value = this.removeTranslationPrefix(value);
					}
					if (mandatory) {
						formArray.controls.push(new FormControl(
							value || '',
							Validators.required
						));
					} else {
						formArray.controls.push(new FormControl(value || ''));
					}
				}
			}
		}
		if (formArray.controls.length === 0) {
			if (mandatory) {
				formArray.controls.push(new FormControl('', Validators.required));
			} else {
				formArray.controls.push(new FormControl(''));
			}
		}
	}

	async actionSaveCurrentOrder() {
		try {
			const rqtOrderObj = this.createRequestOrderObj();
			const order = await firstValueFrom(
				this.eorderApiService.createOrder(rqtOrderObj)
			);
			if (order.id) {
				const orderId = Number(order.id);
				this.orderId = String(orderId);
				this.setOrderId(String(orderId));
			}
			this.snackBar.openFromComponent(
				SnackbarSubmissionFeedbackComponent,
				{
					data: { hideResponseDetails: true,
						isThanksMessage: true,
						isRedirectLink: false,
						submitThanksMessage: this.translationService.getTR('eorder.save.draft.success.message') },
					duration: 20_000,
					panelClass: ['copo-snackbar-submission-feedback-container']
				}
			);
			await this.router.navigateByUrl('/home');
			return true;
		} catch (err) {
			this.errorService.setErrorMsg(
				this.translationService.getTR('eorder.save.draft.error.message'),
				'quapp.error'
			);
			console.error('unable to save order', err);
			return false;
		}
	}

	async actionSubmitOrder() {
		try {
			await firstValueFrom(this.eorderApiService.submitOrder(this.requestOrderObj));
			await this.router.navigateByUrl('/home');
			this.snackBar.openFromComponent(
				SnackbarSubmissionFeedbackComponent,
				{
					data: { hideResponseDetails: true,
						isThanksMessage: true,
						isRedirectLink: false,
						submitThanksMessage: this.translationService.getTR('confirm.thanks') },
					duration: 20_000,
					panelClass: ['copo-snackbar-submission-feedback-container']
				}
			);
			return true;
		} catch (err) {
			if (!(err instanceof HttpErrorResponse)) {
				return false;
			}
			if (typeof err.error === 'object' && err.error !== null && (<{ code: string }> err.error).code) {
				const code: string = (<{ code: string }> err.error).code;
				let errorMessage;
				switch (code) {
				case 'SOLDTOERROR':
					errorMessage = this.translationService.getTR('global.internal.soldto.error');
					break;
				case 'MANDATORY_DATA':
					errorMessage = this.translationService.getTR('eorder.submit.error.msg.rqd.data.missing');
					break;
				case 'BAD_DATA':
					errorMessage = this.translationService.getTR('eorder.submit.error.msg.bad.data');
					break;
				case 'service_needed':
					errorMessage = this.translationService.getTR('global.submit.service.error');
					break;
				default:
					errorMessage = this.translationService.getTR('global.submit.error.unexpected');
				}
				this.errorService.setErrorMsg(errorMessage, 'quapp.error');
			} else if (err.status === 500) {
				this.errorService.setErrorMsg(
					this.translationService.getTR('global.submit.error.unexpected'),
					'global.internal.server.error'
				);
			} else if (err.status === 403) {
				this.errorService.setErrorMsg(
					this.translationService.getTR('global.submit.service.error'),
					'quapp.error'
				);
			} else {
				this.errorService.setErrorMsg(
					this.translationService.getTR('global.submit.error.unexpected'),
					'need4steel.submit.order.error.msg.title'
				);
				console.error(err);
			}
			return false;
		}
	}

	createRequestOrderObj() {
		const selectedProducts = <EorderProduct[]> this.getStateEorderForm().getFormProducts().value;
		const order = this.getStateEorderForm().getEorder();
		const requestOrderItemList: RequestOrderItem[] = [];
		const soldTo = String(order.header.soldTo).padStart(10, '0');

		if (selectedProducts.length > 0) {
			for (const [index, pdt] of selectedProducts.entries()) {
				const requestProductDataList: RequestProductData[] = [];
				if (pdt.steelGrade) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'steelGrade',
						characValueText: pdt.steelGrade,
						characValueUnitTr: '',
						characValueTable: '',
						characValueNum: undefined,
						type: 'string'
					});
				}
				if (pdt.thicknessValue) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'thicknessValue',
						characValueText: '',
						characValueUnitTr: 'mm',
						characValueTable: '',
						characValueNum: pdt.thicknessValue,
						type: 'number'
					});
				}
				if (pdt.widthValue) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'widthValue',
						characValueText: '',
						characValueUnitTr: 'mm',
						characValueTable: '',
						characValueNum: pdt.widthValue,
						type: 'number'
					});
				}
				if (this.getStateEorderForm().getCategory()) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'category',
						characValueText: this.getStateEorderForm().getCategory(),
						characValueUnitTr: '',
						characValueTable: '',
						characValueNum: undefined,
						type: 'string'
					});
				}
				if (this.getStateEorderForm().getSubCategory()) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'subCategory',
						characValueText: this.getStateEorderForm().getSubCategory(),
						characValueUnitTr: '',
						characValueTable: '',
						characValueNum: undefined,
						type: 'string'
					});
				}
				if (this.getStateEorderForm().getFormValue()) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'form',
						characValueText: this.getStateEorderForm().getFormValue(),
						characValueUnitTr: '',
						characValueTable: '',
						characValueNum: undefined,
						type: 'string'
					});
				}

				if (this.getStateEorderForm().getFormValue() === FORM_SHEET_CODE && pdt.lengthValue) {
					requestProductDataList.push({
						id: undefined,
						requestOrderItemId: undefined,
						characName: 'lengthValue',
						characValueText: '',
						characValueUnitTr: 'mm',
						characValueTable: '',
						characValueNum: pdt.lengthValue,
						type: 'number'
					});
				}

				const variableFieldsGroup = pdt.variableFormGroup;
				if (variableFieldsGroup && Object.keys(variableFieldsGroup).length > 0) {
					for (const [fieldKey, fieldValue] of Object.entries(variableFieldsGroup)) {
						if (fieldValue === undefined || fieldValue === '' || fieldValue === null) {
							continue;
						}
						let requestProductData: RequestProductData;
						const type = this.getFieldType(fieldKey, pdt.templateNumber);

						switch (type) {
						case 'string':
							requestProductData = this.createRequestProductData(fieldKey, type, String(fieldValue));
							requestProductDataList.push(requestProductData);
							break;
						case 'number':
							requestProductData = this.createRequestProductData(fieldKey, type, '', <number> fieldValue);
							requestProductDataList.push(requestProductData);
							break;
						default:
							if (Array.isArray(fieldValue) && fieldValue.length > 0) {
								for (const value of fieldValue) {
									const childFieldType = typeof value;
									requestProductData = childFieldType === 'number'
										? this.createRequestProductData(
											fieldKey,
											type,
											'',
											<number> value
										)
										: this.createRequestProductData(
											fieldKey,
											type,
											String(value)
										);
									requestProductDataList.push(requestProductData);
								}
							}
							break;
						}
					}
				}
				if (pdt.templateNumber && pdt.configurationNumber) {
					const templateAndDetail = this.getState().ui.getTemplateNumberDetails(
						pdt.templateNumber
					);
					const configData = this.getState().ui.getConfigurationData(String(pdt.configurationNumber));
					if (!templateAndDetail?.listOfVariables.includes(FIELD_USE_PART_REFERENCE)
						&& configData?.usePartReference) {
						const requestProductData = this.createRequestProductData(
							FIELD_USE_PART_REFERENCE,
							'string',
							configData.usePartReference
						);
						requestProductDataList.push(requestProductData);
					}
				}
				const requestServiceLevelList: RequestServiceLevel[] = [];
				if (pdt.serviceLevel) {
					requestServiceLevelList.push({
						id: 0,
						requestOrderItemId: 0,
						serviceLevelRef: pdt.serviceLevel
					});
				}
				const shipTo = pdt.shipToParty ? String(pdt.shipToParty) : '';
				const appendedShipTo = shipTo ? shipTo.padStart(10, '0') : '';
				let requestedDeliveryDate = '';
				let rddDisplay = '';
				if (pdt.rdd) {
					requestedDeliveryDate = moment(pdt.rdd).format(DATETIME_FORMAT_PATTERN);
					rddDisplay = pdt.rddType === RDD_TYPE_WEEK_VAL
						? moment(pdt.rdd).format(WEEKYEAR_FORMAT_PATTERN)
						: moment(pdt.rdd).format(DATE_FORMAT_PATTERN_DD_MM_YYYY);
				}
				const requestOrderItem: RequestOrderItem = {
					id: 0,
					requestOrderId: 0,
					itemNumber: (index + 1).toString().padStart(3, '0'),
					productRef: '',
					connectionKey: '',
					configurationRef: '',
					templateRef: String(pdt.templateNumber),
					productStatusTr: '',
					soldToRef: soldTo,
					shipToRef: appendedShipTo,
					endUserRef: soldTo,
					transportModeTr: pdt.transportMode || '',
					xcarb: pdt.xcarb || '',
					businessType: pdt.businessType || '',
					deliveryDateTypeTr: pdt.rddType || '',
					deliveryDate: requestedDeliveryDate || undefined,
					deliveryDateDisplay: rddDisplay,
					incotermTr: '',
					incoterm2Tr: '',
					quantityAmount: Number(pdt.quantity),
					totalAmount: this.calculateTotalAmount(pdt.configurationNumber, Number(pdt.quantity)),
					totalUnitTr: 't',
					quantityUnitTr: 't',
					productType: pdt.productType,
					listOfRequestProductData: requestProductDataList,
					listOfRequestServiceLevel: requestServiceLevelList,
				};
				if (pdt.productType === PRODUCT_TYPE_F) {
					requestOrderItem.configurationRef = String(pdt.configurationNumber);
					requestOrderItem.productRef = pdt.inquiryNumber || '';
				}
				requestOrderItemList.push(requestOrderItem);
			}
		}
		this.requestOrderObj = {
			id: 0,
			soldToRef: soldTo,
			purchaseOrderNumber: order.header.customerOrderNumber,
			comments: '',
			listOfRequestOrderItem: requestOrderItemList,
			codeUnitRef: this.boc.referenceCommercialUnit?.codeUnit,
			codeSubUnitRef: this.boc.referenceCommercialSubUnit?.codeSubUnit,
			codeSubSubUnitRef: this.boc.referenceCommercialSubSubUnit?.codeSubSubUnit,
			invoicingCurrencyTr: order.header.currency
		};
		return this.requestOrderObj;
	}

	private initOrder(order: Eorder) {
		this.state = new EorderWizardState(order);
		this.state.isValid = true;
	}

	private removeTranslationPrefix(value: string | undefined): string {
		if (!value || typeof value !== 'string') {
			return '';
		}
		return value.slice(Math.max(0, value.lastIndexOf('.') + 1));
	}

	private prepareVariableFormGroupByType(
		variableFormGroup: FormGroup,
		variableName: string,
		configData: ProductConfiguration,
		variable: TemplateVariable
	) {
		const productType = this.removeTranslationPrefix(configData.productType);
		switch (variable.type) {
		case 'array': {
			let arrayObject: CharacteristicValueSimpleObject[] = [];
			if (configData[variableName] && (productType !== PRODUCT_TYPE_NF || variable.hidden)) {
				arrayObject = <CharacteristicValueSimpleObject[]> configData[variableName];
			}
			const variableArray = <TemplateVariableArray>variable;
			const formArray = new FormArray<FormControl<string | number | null>>([]);

			variableFormGroup.addControl(variableName, formArray);
			const control = variableFormGroup.get(variableName);
			if (control instanceof FormArray) {
				this.prepareVariableFormGroupArray(
					control,
					arrayObject,
					variableArray,
					variable.mandatory
				);
			}
		}
			break;
		case 'number':
		case 'integer':
			if (variableName !== FIELD_THICKNESS && variableName !== FIELD_WIDTH
				&& variableName !== FIELD_LENGTH) {
				let value;
				if (productType !== PRODUCT_TYPE_NF || variable.hidden) {
					value = Number(configData[variableName]);
				}
				if (variable.mandatory) {
					variableFormGroup.addControl(
						variableName,
						new FormControl(value || undefined, Validators.required)
					);
				} else {
					variableFormGroup.addControl(
						variableName,
						new FormControl(value || undefined)
					);
				}
			}
			break;
		case 'string':
			if (variableName !== FIELD_SHIPTO_PARTY && variableName !== FIELD_STEEL_GRADE) {
				let value;
				if (productType !== PRODUCT_TYPE_NF || variable.hidden) {
					value = configData[variableName];
					if (value) {
						if (variable.free) {
							value = this.productCharacteristicsTranslationService.translate(
								!Array.isArray(value) ? String(value) : value.map(v => Object.values(v)).join(',')
							);
						} else if (typeof value === 'string') {
							value = this.removeTranslationPrefix(value);
						}
					}
				}
				if (variable.mandatory) {
					variableFormGroup.addControl(
						variableName,
						new FormControl(value || '', Validators.required)
					);
				} else {
					variableFormGroup.addControl(variableName, new FormControl(value || ''));
				}
			}
			break;
		case 'group':
			this.processGroupVariable(variableFormGroup, variable, configData);
			break;
		}
	}

	private processGroupVariable(
		variableFormGroup: FormGroup,
		groupVariable: TemplateVariable,
		configData: ProductConfiguration
	) {
		if (groupVariable.properties) {
			for (const variableName of Object.getOwnPropertyNames(groupVariable.properties)) {
				const variable = groupVariable.properties[variableName];
				this.prepareVariableFormGroupByType(
					variableFormGroup,
					variableName,
					configData,
					variable
				);
			}
		}
	}

	private calculateTotalAmount(configurationNumber?: number, quantity?: number) {
		if (quantity && configurationNumber
			&& this.getState().ui.hasConfigurationData(String(configurationNumber))) {
			const configData = this.getState().ui.getConfigurationData(String(configurationNumber));
			if (configData?.maxNetWeightValue) {
				return configData.maxNetWeightValue * quantity;
			}
		}
		return 0;
	}

	private createRequestProductData(
		field: string,
		type: string,
		textValue: string,
		numValue?: number
	): RequestProductData {
		return {
			id: undefined,
			requestOrderItemId: undefined,
			characName: field,
			characValueText: textValue,
			characValueUnitTr: '',
			characValueTable: '',
			characValueNum: numValue,
			type
		};
	}

	private getFieldType(fieldName: string, templateNumber?: number) {
		if (templateNumber) {
			const templateAndDetail = this.getState().ui.getTemplateNumberDetails(
				templateNumber
			);
			if (!templateAndDetail) {
				return 'string';
			}
			for (const variableName of Object.getOwnPropertyNames(templateAndDetail.properties)) {
				const variable = templateAndDetail.properties[variableName];
				const variableType = this.getVariableType(variable, fieldName, variableName);
				if (variableType !== '') {
					return variableType;
				}
			}
		}
		return 'string';
	}

	private getVariableType(variable: TemplateVariable, fieldName: string, variableName: string) {
		switch (variable.type) {
		case 'number':
		case 'integer':
		case 'string':
		case 'array':
			if (fieldName === variableName) {
				return variable.type;
			}
			break;
		case 'group':
			for (const entryVariableName of Object.getOwnPropertyNames(variable.properties)) {
				if (fieldName === entryVariableName && variable.properties) {
					const variableLocal = variable.properties[entryVariableName];
					return variableLocal.type;
				}
			}
			break;
		}
		return '';
	}
}
